import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import { PopulationFilter, WorkflowRun, WorkflowTemplate } from '@digital-twins/shared-types'

import { workflowRunsApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import i18n, { defaultContentLanguageFromUi } from '@/i18n'
import { templateSurface } from '@/lib/workflows/templateSurface'
import { useRouter } from '@/routing/navigation'
import { ROUTES } from '@/routes/routes'
import { workflowRunSegmentFields } from './workflowRunSegmentFields'

/**
 * Live run-start action (Phase 2,
 * docs/plans/assistant-platform-v1-phase2-integration-map.md, Step D). Called
 * from `WorkflowDetailPage`'s "Start run" button and from every composer that
 * can arm a workflow (`HomeLabStage`, and `ThreadDetailPage` once its own call
 * site is updated to this shape).
 *
 * `threadToken` is the thread already in scope, when one exists —
 * `ThreadDetailPage` always has one. Omitted, the REQUEST omits it too and
 * `createRun` mints the thread server-side (Step 1 / D0, D1).
 *
 * This used to mint the thread here first, through `knowledgeThreadsApi
 * .create`, which produced a thread titled "Untitled" that only later got a
 * name — and only on the client path. A scheduled fire never runs this hook,
 * so its run had no thread at all and everything that writes to the thread
 * through the run gave up. One place mints now, and it is the place both
 * paths go through, so the manual start and the 07:00 fire produce the same
 * kind of row with the same kind of title.
 *
 * `message` is the composer's typed sentence, if any, mapped onto the
 * template's FIRST `text`-type input only — never split across inputs.
 * `inputs` carries every OTHER value a caller already knows how to fill (for
 * example `HomeLabStage` forwarding the picked segment's `audienceId` into
 * the template's `audience` input, or `WorkflowDetailPage`'s own "Run inputs"
 * form) and is merged in AFTER the message mapping, so an explicit `inputs`
 * entry for the same key would win — a collision that should never actually
 * happen, since `message` only ever targets a `text` input.
 *
 * The audience value contract (confirmed against `twin-test.executor.ts`'s
 * `resolveAudienceId`, `:439-461`): an `audience`-type input is a plain
 * audience/population id string, resolved at EXECUTION time, not at create
 * time — any non-empty string is accepted when the run is created, so
 * `segment.audienceId` (the same species `AudienceSelector.onSelect` emits)
 * is exactly the right shape to forward, with no translation.
 *
 * A required `audience` or `select` input that is STILL EMPTY after the merge
 * refuses outright, with a toast pointing at the workflow's own detail page:
 * sending the request would only earn a 400 the user cannot act on from a
 * composer with no control for it. This is the genuinely-unfillable case, not
 * "any template with such an input" — `HomeLabStage` closes the `audience`
 * case for good composers by forwarding `segment.audienceId`, but nothing yet
 * fills a `select` from any composer, so that one still refuses.
 *
 * Never throws. Every failure — the one-active-run 409, a missing required
 * input, the feature flag, the payment guard — is caught and toasted with the
 * server's own message, mirroring `useHomeLabSubmit`'s "submit never rejects"
 * contract so every call site can fire-and-forget from a synchronous handler.
 *
 * RESOLVES `WorkflowRun | null`, not `void`: the created run (straight off
 * `workflowRunsApi.create`) on success, `null` on every refusal or error path
 * (client-side refusal, the 409/400/403/402 catch, and the re-entrancy no-op),
 * always AFTER the toast has already fired for the cases that show one. This
 * is what lets a caller — `ThreadDetailPage`, honestly lighting its sidebar
 * dot and refreshing its run pane only on a real start — tell "started" from
 * "refused" without re-deriving it from side effects.
 *
 * Never puts the run token in router state: a reload would lose it, and
 * `workflowRunsApi.getForThread` (consumed by `useWorkflowRun`) makes it
 * unnecessary to carry.
 */
export interface StartWorkflowRunInput {
  template: WorkflowTemplate
  /** Thread already in scope. Omitted, `createRun` mints one server-side. */
  threadToken?: string
  /** The composer's typed sentence, if any. Mapped onto the first `text` input. */
  message?: string
  /**
   * How the run was started. Defaults to `'user'` (the lobby). A thread action
   * armed from the command menu passes `'thread_action'`, the same tag the
   * menu's own start-now path writes.
   */
  trigger?: 'user' | 'thread_action'
  /**
   * Extra input values, keyed by `WorkflowInputSpec.key`, merged in AFTER the
   * `message` mapping above. Optional so every existing caller (and
   * `ThreadDetailPage`'s `{template, threadToken, message}` call, wired by a
   * concurrent change) keeps compiling unchanged.
   */
  inputs?: Record<string, string | null>
  /**
   * Content language for a thread `createRun` mints. An explicit
   * LanguageSwapper pick wins; omitted defaults from the account UI language.
   * Ignored when `threadToken` is already in scope, since that thread already
   * has a language of its own.
   */
  language?: string
  /**
   * Structured segment from confirm / `commandSeed`. Own DTO keys — never
   * folded into `message`, `inputs`, or `rest`.
   */
  populationFilter?: PopulationFilter
  segmentLabel?: string
  /**
   * Today's note (this run, this person). Passed through to create-run and
   * never persisted, never mapped onto a template input. Whitespace / omitted
   * is treated as absent.
   */
  note?: string
}

export interface UseStartWorkflowRunResult {
  isStarting: boolean
  /** Resolves the created run on success, `null` on every refusal or error path. */
  startRun: (input: StartWorkflowRunInput) => Promise<WorkflowRun | null>
}

/** A real, non-empty value — `undefined`, `null` and `''` all count as "not filled". */
function hasValue(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0
}

/**
 * True when a required `audience`, `select`, or `stimulus` input is still empty in the
 * MERGED inputs (message mapping + caller-supplied `inputs`). `text` is
 * excluded: it is always fillable, either by the composer's own textarea or
 * (on `WorkflowDetailPage`, which has its own "Run inputs" form) by the same
 * 400 an empty required field would earn there too.
 *
 * Exported (D1) so an off-thread composer that hands off rather than calling
 * `startRun` — it never mints a thread through this hook once one is
 * already armed — can refuse with the identical "needs setup" toast BEFORE
 * minting, using the same merged shape `mergeStartInputs` below builds.
 */
export function hasUnfillableRequiredInput(
  template: WorkflowTemplate,
  mergedInputs: Record<string, string | null>,
): boolean {
  return template.inputs.some(
    (spec) =>
      spec.required &&
      (spec.type === 'audience' ||
        spec.type === 'select' ||
        spec.type === 'stimulus') &&
      !hasValue(mergedInputs[spec.key]),
  )
}

/**
 * `message` mapped onto the template's FIRST `text` input, then
 * `inputs` merged in AFTER — so an explicit `inputs` entry for the same key
 * wins, a collision that should never actually happen since `message` only
 * ever targets a `text` input. The exact shape `startRun` builds internally;
 * exported (D1) so an off-thread composer can run `hasUnfillableRequiredInput`
 * against it before minting a thread, rather than after.
 */
export function mergeStartInputs(
  template: WorkflowTemplate,
  message: string | undefined,
  inputs: Record<string, string | null> | undefined,
): Record<string, string | null> {
  const textInputKey = template.inputs.find((spec) => spec.type === 'text')?.key
  const trimmedMessage = message?.trim()
  return {
    ...(textInputKey && trimmedMessage ? { [textInputKey]: trimmedMessage } : {}),
    ...inputs,
  }
}

export function useStartWorkflowRun(): UseStartWorkflowRunResult {
  const router = useRouter()
  const [isStarting, setIsStarting] = useState(false)
  /* Re-entrancy guard, mirroring `useHomeLabSubmit`'s own `inFlightRef`: a ref
     rather than state because it must read true synchronously within the same
     tick a second click would land in. */
  const inFlightRef = useRef(false)

  const startRun = useCallback(
    async (input: StartWorkflowRunInput): Promise<WorkflowRun | null> => {
      if (inFlightRef.current) {
        return null
      }

      /* `message` mapping FIRST, caller-supplied `inputs` merged in AFTER —
         per this hook's own contract, so an explicit `inputs` entry wins on
         the (should-never-happen) collision. */
      /* `WorkflowTemplate` carries no `surface` (that field is on
         `WorkflowTemplateSummary`), so passing it here was always `undefined`
         and only ever produced a type error. Classify by slug, exactly as
         `ThreadCommandMenu` does. */
      const isLobbyTemplate = templateSurface({ slug: input.template.slug }) === 'lobby'
      const mergedInputs = mergeStartInputs(
        input.template,
        input.message,
        input.inputs,
      )
      /* Lobby's exemption is about starting BARE from the lobby, where there is
         no composer and no form to fill: it is not a property of the template's
         grouping. `surface` is grouping only, so a caller that handed us a
         message or explicit inputs meant them, and a thread arming a lobby
         template (WorkflowMenu offers exactly those) must keep both. */
      const startsBare = isLobbyTemplate && Object.keys(mergedInputs).length === 0

      if (!startsBare && hasUnfillableRequiredInput(input.template, mergedInputs)) {
        toast.error(i18n.t('workflows.run.needsSetup', { ns: 'app', name: input.template.displayName }), {
          description: i18n.t('workflows.run.openToFillRest', { ns: 'app' }),
          action: {
            label: i18n.t('workflows.run.openWorkflow', { ns: 'app' }),
            onClick: () => router.push(ROUTES.workflow(input.template.slug)),
          },
        })
        return null
      }

      inFlightRef.current = true
      setIsStarting(true)

      try {
        const hasInputs = !startsBare && Object.keys(mergedInputs).length > 0
        const trimmedNote =
          typeof input.note === 'string' ? input.note.trim() : ''
        /* Direct-start only: the typed sentence becomes the persisted ask
           turn. Intake-settled callers (`confirmIntakeDirection`) omit
           `message` and must not send this — the intake turn already stored
           the ask. Whitespace is absent, same as `note`. */
        const trimmedMessage =
          typeof input.message === 'string' ? input.message.trim() : ''

        const run = await workflowRunsApi.create({
          templateToken: input.template.token,
          /* Omitted, not null: absence is what tells `createRun` to mint. */
          ...(input.threadToken ? { threadToken: input.threadToken } : {}),
          /* Only meaningful on the minting path, where it is the new thread's
             answer language. An explicit pick wins over the UI chrome. */
          ...(input.threadToken
            ? {}
            : {
                language:
                  input.language ?? defaultContentLanguageFromUi(i18n.language),
              }),
          ...(hasInputs ? { inputs: mergedInputs } : {}),
          trigger: input.trigger ?? 'user',
          ...workflowRunSegmentFields(input),
          ...(trimmedNote ? { note: trimmedNote } : {}),
          ...(trimmedMessage ? { askTurn: trimmedMessage } : {}),
        })

        /* The run reports the thread it was bound to, minted or supplied, so
           there is no second source of truth to keep in step here. */
        const threadToken = input.threadToken ?? run.threadToken
        if (threadToken) {
          router.push(ROUTES.thread(threadToken))
        }
        return run
      } catch (error) {
        toast.error(getApiErrorMessage(error, i18n.t('workflows.run.couldNotStart', { ns: 'app' })))
        return null
      } finally {
        setIsStarting(false)
        inFlightRef.current = false
      }
    },
    [router],
  )

  return { isStarting, startRun }
}
